import type * as interfaces from '../../ts_interfaces/index.js';
import * as plugins from '../plugins.js';

declare global {
  interface HTMLElementTagNameMap {
    'maintainable-page': MaintainablePage;
  }
}

interface IStat {
  value: string;
  label: string;
}

interface IPillar {
  number: string;
  title: string;
  body: string;
}

interface IFeature {
  title: string;
  body: string;
}

type TInterestState =
  | 'idle'
  | 'submitting'
  | 'verification-sent'
  | 'awaiting-confirmation'
  | 'confirming'
  | 'verified'
  | 'invalid'
  | 'error';

const stats: IStat[] = [
  {
    value: '−38%',
    label: 'Target lifetime operating cost vs. conventional builds',
  },
  {
    value: '50 yr',
    label: 'Design horizon for every structural and mechanical system',
  },
  {
    value: '0',
    label: 'Walls that need cutting for a repair — dust never enters your home',
  },
];

const pillars: IPillar[] = [
  {
    number: '01',
    title: 'Dustless repairs',
    body:
      'A double-floor system carries plumbing, electrical, and HVAC beneath removable panels. Every line is reachable without cutting a wall — no demolition, no dust, repairs in hours.',
  },
  {
    number: '02',
    title: 'Standard by design',
    body:
      'Common components across all properties mean parts are always in stock and any qualified tradesperson can do the work. No bespoke fittings, no orphaned parts.',
  },
  {
    number: '03',
    title: 'Priced for fifty years',
    body:
      'We select materials on total cost of ownership, not sticker price. Durable envelopes, passive efficiency, and finishes that wear in instead of wearing out.',
  },
];

const healthFeatures: IFeature[] = [
  {
    title: 'HEPA air filtration',
    body: 'Whole-home HEPA filtration removes fine dust, pollen, and particulates before air reaches any room.',
  },
  {
    title: 'Controlled humidity',
    body: 'Humidity held in the healthy band year-round — no dry winters, no damp summers, no mold.',
  },
  {
    title: 'Temperature per room',
    body: 'Each room has its own zone. Sleep cool, work warm — without arguing with a single thermostat.',
  },
  {
    title: 'Quiet air conditioning',
    body: 'Ducted, low-noise cooling designed into the structure, serviced from outside your living space.',
  },
];

const dcFeatures: IStat[] = [
  {
    value: '1 kW',
    label: 'Of rack cooling reserved per tenant',
  },
  {
    value: '24/7',
    label: 'Monitored power, cooling, and connectivity',
  },
  {
    value: 'Yours',
    label: 'Bring your own hardware — AI, storage, home lab',
  },
];

const investorPoints = [
  'Operating costs 30–40% below comparable stock',
  'Predictable maintenance schedules, no deferred-liability surprises',
  'Assets appraised on longevity, not just location',
];

@plugins.deesElement.customElement('maintainable-page')
export class MaintainablePage extends plugins.deesElement.DeesElement {
  @plugins.deesElement.state()
  private accessor interestState: TInterestState = 'idle';

  @plugins.deesElement.state()
  private accessor interestError = '';

  private verificationToken = '';

  public static styles = [
    plugins.deesElement.cssManager.defaultStyles,
    plugins.deesElement.css`
      :host {
        --paper: #faf8f4;
        --ink: #1c1a16;
        --ink-soft: #4b463d;
        --ink-muted: #6b6559;
        --ink-faint: #9a9385;
        --green: #4a5d3a;
        --green-soft: #a8b894;
        --line: #ddd6c8;
        --line-dark: #c9c2b4;
        --panel: #efebe1;
        --success: #dce3d3;
        --max-width: 1440px;
        --gutter: 56px;
        --serif: 'Instrument Serif', Georgia, 'Times New Roman', serif;
        --sans: 'Archivo', 'Helvetica Neue', Arial, sans-serif;

        display: block;
        min-width: 320px;
        overflow: clip;
        background: var(--paper);
        color: var(--ink);
        font-family: var(--sans);
        -webkit-font-smoothing: antialiased;
        text-rendering: optimizeLegibility;
      }

      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }

      a {
        color: inherit;
        text-decoration: none;
      }

      a,
      button,
      input {
        -webkit-tap-highlight-color: transparent;
      }

      a:focus-visible,
      button:focus-visible,
      input:focus-visible {
        outline: 2px solid var(--green);
        outline-offset: 4px;
      }

      section,
      header,
      [id] {
        scroll-margin-top: 24px;
      }

      figure {
        margin: 0;
      }

      .shell {
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
        padding-inline: var(--gutter);
      }

      .wordmark {
        font-family: var(--serif);
        letter-spacing: -0.01em;
      }

      .wordmark-dot {
        color: var(--green);
      }

      .eyebrow {
        margin: 0;
        color: var(--green);
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }

      .display-heading {
        margin: 0;
        font-family: var(--serif);
        font-weight: 400;
        letter-spacing: -0.01em;
        line-height: 1.08;
        text-wrap: pretty;
      }

      .nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 92px;
        padding-block: 24px;
      }

      .nav .wordmark {
        font-size: 24px;
      }

      .nav-links {
        display: flex;
        align-items: center;
        gap: 36px;
        font-size: 14px;
        letter-spacing: 0.02em;
      }

      .nav-link {
        opacity: 0.75;
        transition:
          color 160ms ease,
          opacity 160ms ease;
      }

      .nav-link:hover {
        color: var(--green);
        opacity: 1;
      }

      .button {
        display: inline-flex;
        min-height: 50px;
        align-items: center;
        justify-content: center;
        padding: 15px 29px;
        border: 1px solid var(--line-dark);
        border-radius: 999px;
        background: transparent;
        color: var(--ink);
        font-family: var(--sans);
        font-size: 15px;
        font-weight: 500;
        line-height: 1.2;
        cursor: pointer;
        transition:
          background 160ms ease,
          border-color 160ms ease,
          color 160ms ease;
      }

      .button:hover {
        border-color: var(--ink);
      }

      .button-primary {
        border-color: var(--ink);
        background: var(--ink);
        color: var(--paper);
      }

      .button-primary:hover {
        border-color: var(--green);
        background: var(--green);
        color: var(--paper);
      }

      .nav-cta {
        min-height: 40px;
        padding: 9px 21px;
        border-color: var(--ink);
        font-size: 14px;
      }

      .hero {
        padding-top: 40px;
      }

      .planning-badge {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 28px;
        padding: 8px 18px;
        border: 1px solid var(--line-dark);
        border-radius: 999px;
        color: var(--green);
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .planning-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--green);
      }

      .hero h1 {
        max-width: 15ch;
        margin: 0;
        font-family: var(--serif);
        font-size: clamp(52px, 7.5vw, 108px);
        font-weight: 400;
        letter-spacing: -0.02em;
        line-height: 1.02;
        text-wrap: pretty;
      }

      .hero h1 em {
        color: var(--green);
        font-weight: 400;
      }

      .hero-bottom {
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: end;
        gap: 48px;
        margin-top: 40px;
      }

      .hero-summary {
        max-width: 46ch;
        margin: 0;
        color: var(--ink-soft);
        font-size: 19px;
        line-height: 1.6;
        text-wrap: pretty;
      }

      .hero-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding-bottom: 4px;
      }

      .hero-image {
        aspect-ratio: 21 / 9;
        margin-top: 56px;
        overflow: hidden;
        border-radius: 4px;
        background: #e7e2d8;
      }

      .hero-image img,
      .health-image img,
      .datacenter-image img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .stats {
        padding-block: 72px;
      }

      .stats-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 40px;
        padding-top: 48px;
        border-top: 1px solid var(--line);
      }

      .stat {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .stat-value {
        font-family: var(--serif);
        font-size: 56px;
        line-height: 1;
      }

      .stat-label {
        max-width: 30ch;
        color: var(--ink-muted);
        font-size: 14px;
        letter-spacing: 0.02em;
      }

      .stats-note {
        margin: 24px 0 0;
        color: var(--ink-faint);
        font-size: 12px;
      }

      .approach {
        background: var(--ink);
        color: var(--paper);
      }

      .approach-inner {
        padding-block: 96px;
      }

      .approach .eyebrow {
        margin-bottom: 20px;
        color: var(--green-soft);
      }

      .approach h2 {
        max-width: 22ch;
        margin-bottom: 64px;
        font-size: clamp(36px, 4.5vw, 60px);
      }

      .pillars {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 56px;
      }

      .pillar {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-top: 24px;
        border-top: 1px solid rgb(250 248 244 / 20%);
      }

      .pillar-number {
        color: var(--green-soft);
        font-family: var(--serif);
        font-size: 20px;
      }

      .pillar h3,
      .health-feature h3 {
        margin: 0;
        font-family: var(--serif);
        font-weight: 400;
      }

      .pillar h3 {
        font-size: 28px;
      }

      .pillar p {
        margin: 0;
        color: rgb(250 248 244 / 70%);
        font-size: 15px;
        line-height: 1.65;
        text-wrap: pretty;
      }

      .health {
        display: grid;
        grid-template-columns: 1fr 1.1fr;
        align-items: center;
        gap: 64px;
        padding-block: 96px;
      }

      .health-image {
        aspect-ratio: 4 / 5;
        overflow: hidden;
        border-radius: 4px;
        background: #e7e2d8;
      }

      .health-copy {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .health h2,
      .investors h2 {
        font-size: clamp(34px, 4vw, 52px);
      }

      .health-summary {
        margin: 0;
        color: var(--ink-soft);
        font-size: 16px;
        line-height: 1.7;
        text-wrap: pretty;
      }

      .health-features {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin-top: 12px;
      }

      .health-feature {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding-top: 16px;
        border-top: 1px solid var(--line);
      }

      .health-feature h3 {
        font-size: 21px;
      }

      .health-feature p {
        margin: 0;
        color: var(--ink-muted);
        font-size: 14px;
        line-height: 1.6;
        text-wrap: pretty;
      }

      .datacenter {
        background: var(--ink);
        color: var(--paper);
      }

      .datacenter-inner {
        display: grid;
        grid-template-columns: 1.1fr 1fr;
        align-items: center;
        gap: 64px;
        padding-block: 96px;
      }

      .datacenter-copy {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .datacenter .eyebrow {
        color: var(--green-soft);
      }

      .datacenter h2 {
        font-size: clamp(34px, 4vw, 52px);
      }

      .datacenter-summary {
        margin: 0;
        color: rgb(250 248 244 / 70%);
        font-size: 16px;
        line-height: 1.7;
        text-wrap: pretty;
      }

      .datacenter-features {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
        margin-top: 12px;
      }

      .datacenter-feature {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding-top: 16px;
        border-top: 1px solid rgb(250 248 244 / 20%);
      }

      .datacenter-feature-value {
        font-family: var(--serif);
        font-size: 26px;
      }

      .datacenter-feature-label {
        color: rgb(250 248 244 / 60%);
        font-size: 13px;
        line-height: 1.5;
      }

      .datacenter-image {
        aspect-ratio: 4 / 5;
        overflow: hidden;
        border-radius: 4px;
        background: #26231e;
      }

      .investors {
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: start;
        gap: 64px;
        padding-top: 72px;
        padding-bottom: 96px;
        border-top: 1px solid var(--line);
      }

      .investor-heading {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .investor-copy {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding-top: 8px;
      }

      .investor-summary {
        margin: 0;
        color: var(--ink-soft);
        font-size: 17px;
        line-height: 1.7;
        text-wrap: pretty;
      }

      .investor-points {
        display: flex;
        flex-direction: column;
        gap: 14px;
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .investor-point {
        display: flex;
        align-items: baseline;
        gap: 14px;
        color: var(--ink);
        font-size: 15px;
        line-height: 1.5;
      }

      .investor-point::before {
        width: 6px;
        height: 6px;
        flex: 0 0 6px;
        border-radius: 50%;
        background: var(--green);
        content: '';
        transform: translateY(-2px);
      }

      .house-spec {
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: start;
        gap: 64px;
        padding-top: 72px;
        padding-bottom: 96px;
        border-top: 1px solid var(--line);
      }

      .house-spec-heading,
      .house-spec-copy {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .house-spec-copy {
        align-items: flex-start;
        gap: 24px;
        padding-top: 8px;
      }

      .house-spec-summary {
        margin: 0;
        color: var(--ink-soft);
        font-size: 17px;
        line-height: 1.7;
        text-wrap: pretty;
      }

      .house-spec-link {
        display: inline-flex;
        align-items: center;
        gap: 10px;
      }

      .interest {
        background: var(--panel);
      }

      .interest-inner {
        display: flex;
        max-width: 900px;
        flex-direction: column;
        align-items: center;
        gap: 24px;
        margin: 0 auto;
        padding: 104px 56px;
        text-align: center;
      }

      .interest h2 {
        font-size: clamp(36px, 4.5vw, 56px);
      }

      .interest-copy {
        max-width: 52ch;
        margin: 0;
        color: var(--ink-soft);
        font-size: 16px;
        line-height: 1.6;
        text-wrap: pretty;
      }

      .interest-form {
        display: flex;
        width: 100%;
        max-width: 480px;
        gap: 10px;
        margin-top: 8px;
      }

      .interest-form input {
        min-width: 0;
        flex: 1;
        padding: 15px 21px;
        border: 1px solid var(--line-dark);
        border-radius: 999px;
        background: var(--paper);
        color: var(--ink);
        font-family: var(--sans);
        font-size: 15px;
      }

      .interest-form input::placeholder {
        color: var(--ink-faint);
      }

      .interest-form input:focus {
        border-color: var(--green);
      }

      .interest-form button[disabled] {
        cursor: wait;
        opacity: 0.68;
      }

      .interest-honeypot {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
      }

      .interest-status {
        display: flex;
        max-width: 620px;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        margin-top: 8px;
        padding: 16px 32px;
        border-radius: 24px;
        background: var(--success);
        color: #33422a;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.5;
      }

      .interest-status p {
        margin: 0;
      }

      .interest-status-error {
        border: 1px solid #bb9588;
        background: #f3e4df;
        color: #65382b;
      }

      .privacy-note {
        max-width: 58ch;
        margin: -8px 0 0;
        color: var(--ink-muted);
        font-size: 12px;
        line-height: 1.5;
      }

      .privacy-note a {
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      .footer {
        border-top: 1px solid var(--line);
      }

      .footer-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 28px;
        min-height: 92px;
        padding-block: 28px;
        color: var(--ink-muted);
        font-size: 13px;
      }

      .footer .wordmark {
        color: var(--ink);
        font-size: 18px;
      }

      .footer-links {
        display: flex;
        gap: 28px;
      }

      .footer-links a,
      .legal-link {
        color: var(--ink-muted);
        transition: color 160ms ease;
      }

      .footer-links a:hover,
      .legal-link:hover {
        color: var(--ink);
      }

      .legal {
        text-align: right;
      }

      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      @media (max-width: 900px) {
        :host {
          --gutter: 36px;
        }

        .nav-links {
          gap: 22px;
        }

        .hero-bottom {
          grid-template-columns: 1fr;
          gap: 28px;
        }

        .hero-actions {
          justify-content: flex-start;
        }

        .health {
          grid-template-columns: 0.9fr 1.1fr;
          gap: 40px;
        }

        .datacenter-inner {
          gap: 40px;
        }

        .investors,
        .house-spec {
          gap: 40px;
        }

        .footer-inner {
          flex-wrap: wrap;
        }
      }

      @media (max-width: 720px) {
        :host {
          --gutter: 20px;
        }

        .nav {
          min-height: 72px;
          padding-block: 16px;
        }

        .nav .wordmark {
          font-size: 22px;
        }

        .nav-link {
          display: none;
        }

        .nav-links {
          gap: 0;
        }

        .nav-cta {
          min-height: 38px;
          padding: 8px 16px;
          font-size: 13px;
        }

        .hero {
          padding-top: 24px;
        }

        .planning-badge {
          margin-bottom: 22px;
          padding: 7px 14px;
          font-size: 11px;
        }

        .hero h1 {
          font-size: clamp(48px, 16vw, 68px);
        }

        .hero-bottom {
          margin-top: 28px;
        }

        .hero-summary {
          font-size: 17px;
          line-height: 1.55;
        }

        .hero-actions {
          flex-wrap: wrap;
        }

        .button {
          min-height: 46px;
          padding: 13px 22px;
          font-size: 14px;
        }

        .hero-image {
          aspect-ratio: 4 / 3;
          margin-top: 40px;
        }

        .stats {
          padding-block: 56px;
        }

        .stats-grid {
          grid-template-columns: 1fr;
          gap: 30px;
          padding-top: 36px;
        }

        .stat {
          display: grid;
          grid-template-columns: 110px 1fr;
          align-items: baseline;
        }

        .stat-value {
          font-size: 46px;
        }

        .approach-inner {
          padding-block: 72px;
        }

        .approach h2 {
          margin-bottom: 48px;
          font-size: 42px;
        }

        .pillars {
          grid-template-columns: 1fr;
          gap: 34px;
        }

        .health {
          grid-template-columns: 1fr;
          gap: 48px;
          padding-block: 72px;
        }

        .health h2,
        .datacenter h2,
        .investors h2 {
          font-size: 40px;
        }

        .health-features {
          grid-template-columns: 1fr;
          gap: 22px;
        }

        .datacenter-inner {
          grid-template-columns: 1fr;
          gap: 48px;
          padding-block: 72px;
        }

        .datacenter-features {
          grid-template-columns: 1fr;
          gap: 22px;
        }

        .investors,
        .house-spec {
          grid-template-columns: 1fr;
          gap: 38px;
          padding-top: 56px;
          padding-bottom: 72px;
        }

        .house-spec h2 {
          font-size: 40px;
        }

        .house-spec-copy {
          padding-top: 0;
        }

        .interest-inner {
          padding: 76px 20px;
        }

        .interest h2 {
          font-size: 40px;
        }

        .interest-form {
          flex-direction: column;
        }

        .interest-form input,
        .interest-form .button {
          width: 100%;
        }

        .footer-inner {
          align-items: flex-start;
          flex-direction: column;
          gap: 20px;
          padding-block: 32px;
        }

        .footer-links {
          flex-wrap: wrap;
          gap: 12px 22px;
        }

        .legal {
          text-align: left;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
          scroll-behavior: auto !important;
          transition-duration: 0.01ms !important;
        }
      }
    `,
  ];

  public override async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    const token = new URLSearchParams(globalThis.location.hash.slice(1)).get(
      'interest-verification',
    );
    if (!token) return;
    this.verificationToken = token;
    this.interestState = 'awaiting-confirmation';
    globalThis.history.replaceState(
      globalThis.history.state,
      '',
      `${globalThis.location.pathname}${globalThis.location.search}#interest`,
    );
  }

  private async sendInterestRequest(
    emailArg: string,
    websiteArg: string,
  ): Promise<void> {
    const request =
      new plugins.typedrequest.TypedRequest<interfaces.IReq_RequestInterestVerification>(
        '/typedrequest',
        'requestInterestVerification',
      );
    await request.fire({
      email: emailArg,
      website: websiteArg,
    });
  }

  private async sendConfirmationRequest(tokenArg: string): Promise<'verified' | 'invalid'> {
    const request =
      new plugins.typedrequest.TypedRequest<interfaces.IReq_ConfirmInterestVerification>(
        '/typedrequest',
        'confirmInterestVerification',
      );
    const response = await request.fire({ token: tokenArg });
    return response.status;
  }

  private async handleInterestSubmit(eventArg: SubmitEvent): Promise<void> {
    eventArg.preventDefault();
    const form = eventArg.currentTarget as HTMLFormElement;
    if (!form.reportValidity()) return;
    const formData = new FormData(form);
    this.interestState = 'submitting';
    this.interestError = '';
    try {
      await this.sendInterestRequest(
        String(formData.get('email') || ''),
        String(formData.get('website') || ''),
      );
      this.interestState = 'verification-sent';
    } catch {
      this.interestError =
        'We could not send the verification email. Please try again in a moment.';
      this.interestState = 'error';
    }
  }

  private async handleInterestConfirmation(): Promise<void> {
    if (!this.verificationToken) {
      this.interestState = 'invalid';
      return;
    }
    this.interestError = '';
    this.interestState = 'confirming';
    try {
      const status = await this.sendConfirmationRequest(this.verificationToken);
      this.verificationToken = '';
      this.interestState = status;
    } catch {
      this.interestError = 'We could not confirm your email. Please try again in a moment.';
      this.interestState = 'awaiting-confirmation';
    }
  }

  private renderInterestForm(): plugins.deesElement.TemplateResult {
    const submitting = this.interestState === 'submitting';
    return plugins.deesElement.html`
      <form
        class="interest-form"
        @submit=${(eventArg: SubmitEvent) => this.handleInterestSubmit(eventArg)}
      >
        <label class="visually-hidden" for="interest-email">Email address</label>
        <input
          id="interest-email"
          name="email"
          type="email"
          autocomplete="email"
          required
          ?disabled=${submitting}
          placeholder="you@example.com"
        />
        <div class="interest-honeypot" aria-hidden="true">
          <label for="interest-website">Website</label>
          <input
            id="interest-website"
            name="website"
            type="text"
            autocomplete="off"
            tabindex="-1"
          />
        </div>
        <button class="button button-primary" type="submit" ?disabled=${submitting}>
          ${submitting ? 'Sending…' : 'Show interest'}
        </button>
      </form>
      ${this.interestState === 'error'
        ? plugins.deesElement.html`
            <div class="interest-status interest-status-error" role="alert">
              <p>${this.interestError}</p>
            </div>
          `
        : plugins.deesElement.html``}
    `;
  }

  private renderInterestAction(): plugins.deesElement.TemplateResult {
    if (
      this.interestState === 'awaiting-confirmation'
      || this.interestState === 'confirming'
    ) {
      return plugins.deesElement.html`
        <div class="interest-status" role="status">
          <p>One last step: confirm that you want to join the interest list.</p>
          ${this.interestError
            ? plugins.deesElement.html`<p class="interest-error">${this.interestError}</p>`
            : plugins.deesElement.html``}
          <button
            class="button button-primary"
            type="button"
            ?disabled=${this.interestState === 'confirming'}
            @click=${() => this.handleInterestConfirmation()}
          >
            ${this.interestState === 'confirming' ? 'Confirming…' : 'Confirm email'}
          </button>
        </div>
      `;
    }
    if (this.interestState === 'verification-sent') {
      return plugins.deesElement.html`
        <div class="interest-status" role="status">
          <p>Check your inbox — we sent you a link to verify your email.</p>
        </div>
      `;
    }
    if (this.interestState === 'verified') {
      return plugins.deesElement.html`
        <div class="interest-status" role="status">
          <p>Email verified — you're on the list.</p>
        </div>
      `;
    }
    if (this.interestState === 'invalid') {
      return plugins.deesElement.html`
        <div class="interest-status interest-status-error" role="alert">
          <p>This verification link is invalid or expired. Please request a new one.</p>
        </div>
        ${this.renderInterestForm()}
      `;
    }
    return this.renderInterestForm();
  }

  public render(): plugins.deesElement.TemplateResult {
    return plugins.deesElement.html`
      <nav class="shell nav" aria-label="Primary navigation">
        <a class="wordmark" href="#top" aria-label="maintainable.xyz home">
          maintainable<span class="wordmark-dot">.xyz</span>
        </a>
        <div class="nav-links">
          <a class="nav-link" href="#approach">Approach</a>
          <a class="nav-link" href="#health">Healthy living</a>
          <a class="nav-link" href="#investors">Investors</a>
          <a class="nav-link" href="#spec">House spec</a>
          <a class="button nav-cta" href="#interest">Show interest</a>
        </div>
      </nav>

      <header id="top" class="shell hero">
        <div class="planning-badge">
          <span class="planning-dot" aria-hidden="true"></span>
          Now in planning
        </div>
        <h1>Buildings that get <em>better</em> with age.</h1>
        <div class="hero-bottom">
          <p class="hero-summary">
            We're planning our first spaces engineered for durability and healthy living —
            dustless repairs, HEPA-filtered air, and room-by-room climate control. Show your
            interest now and help shape where we build first.
          </p>
          <div class="hero-actions">
            <a class="button button-primary" href="#interest">Show interest</a>
            <a class="button" href="#approach">Our approach</a>
          </div>
        </div>
        <figure class="hero-image">
          <img
            src="/architectural-facade.png"
            alt="Contemporary apartment building framed by mature trees"
            width="1915"
            height="821"
            fetchpriority="high"
          />
        </figure>
      </header>

      <section class="shell stats" aria-label="Design targets">
        <div class="stats-grid">
          ${stats.map(
            (stat) => plugins.deesElement.html`
              <div class="stat">
                <span class="stat-value">${stat.value}</span>
                <span class="stat-label">${stat.label}</span>
              </div>
            `,
          )}
        </div>
        <p class="stats-note">
          Targets based on our design standards — first properties currently in planning.
        </p>
      </section>

      <section id="approach" class="approach">
        <div class="shell approach-inner">
          <p class="eyebrow">Our approach</p>
          <h2 class="display-heading">
            Maintainability isn't a feature. It's the foundation of every decision we make.
          </h2>
          <div class="pillars">
            ${pillars.map(
              (pillar) => plugins.deesElement.html`
                <article class="pillar">
                  <span class="pillar-number">${pillar.number}</span>
                  <h3>${pillar.title}</h3>
                  <p>${pillar.body}</p>
                </article>
              `,
            )}
          </div>
        </div>
      </section>

      <section id="health" class="shell health">
        <figure class="health-image">
          <img
            src="/healthy-interior.webp"
            alt="Bright, calm living space with integrated ventilation"
            width="1000"
            height="1250"
            loading="lazy"
          />
        </figure>
        <div class="health-copy">
          <p class="eyebrow">Healthy by design</p>
          <h2 class="display-heading">The healthiest air you'll breathe is indoors.</h2>
          <p class="health-summary">
            Every space is a controlled environment — filtered, balanced, and tuned room by room.
            And when something needs fixing, it's fixed without dust ever entering your home.
          </p>
          <div class="health-features">
            ${healthFeatures.map(
              (feature) => plugins.deesElement.html`
                <article class="health-feature">
                  <h3>${feature.title}</h3>
                  <p>${feature.body}</p>
                </article>
              `,
            )}
          </div>
        </div>
      </section>

      <section id="datacenter" class="datacenter">
        <div class="shell datacenter-inner">
          <div class="datacenter-copy">
            <p class="eyebrow">In every cellar</p>
            <h2 class="display-heading">A mini datacenter under every building.</h2>
            <p class="datacenter-summary">
              Each cellar houses a small, professionally cooled server room — colocation for
              tenants and infrastructure for decentralized AI applications. Run your own hardware
              steps from your door, with the building handling power and cooling.
            </p>
            <div class="datacenter-features">
              ${dcFeatures.map(
                (feature) => plugins.deesElement.html`
                  <div class="datacenter-feature">
                    <span class="datacenter-feature-value">${feature.value}</span>
                    <span class="datacenter-feature-label">${feature.label}</span>
                  </div>
                `,
              )}
            </div>
          </div>
          <figure class="datacenter-image">
            <img
              src="/cellar-datacenter.webp"
              alt="Professionally cooled server racks in a building cellar"
              width="1000"
              height="1250"
              loading="lazy"
            />
          </figure>
        </div>
      </section>

      <section id="investors" class="shell investors">
        <div class="investor-heading">
          <p class="eyebrow">For investors</p>
          <h2 class="display-heading">
            Durability is the most underpriced asset in real estate.
          </h2>
        </div>
        <div class="investor-copy">
          <p class="investor-summary">
            Most buildings lose value to deferred maintenance. Ours are engineered against it:
            standardized components, accessible systems, and materials chosen for their
            fifty-year cost — not their day-one price. The result is lower operating expense,
            longer asset life, and tenants who stay.
          </p>
          <ul class="investor-points">
            ${investorPoints.map(
              (point) => plugins.deesElement.html`
                <li class="investor-point">${point}</li>
              `,
            )}
          </ul>
        </div>
      </section>

      <section id="spec" class="shell house-spec">
        <div class="house-spec-heading">
          <p class="eyebrow">Open specification</p>
          <h2 class="display-heading">
            The house spec is public. Read it, question it, improve it.
          </h2>
        </div>
        <div class="house-spec-copy">
          <p class="house-spec-summary">
            Everything we plan to build is written down — the double-floor system, air
            handling, room-by-room climate, the cellar datacenter — as an open specification
            anyone can read. We build in the open because buildings should outlive the companies
            that make them.
          </p>
          <a
            class="button house-spec-link"
            href="https://foss.global/#/repo/maintainable.xyz/house-spec"
            target="_blank"
            rel="noopener"
          >
            Read the house spec <span aria-hidden="true">→</span>
          </a>
        </div>
      </section>

      <section id="interest" class="interest">
        <div class="interest-inner">
          <h2 class="display-heading">Interested in renting a maintainable space?</h2>
          <p class="interest-copy">
            Leave your email and we'll let you know when units open in your area. No spam — one
            note when it matters.
          </p>
          ${this.renderInterestAction()}
          <p class="privacy-note">
            We store your address only after you confirm it. You can withdraw at any time by
            emailing <a href="mailto:hello@task.vc">hello@task.vc</a>.
          </p>
        </div>
      </section>

      <footer class="footer">
        <div class="shell footer-inner">
          <a class="wordmark" href="#top" aria-label="Back to top">
            maintainable<span class="wordmark-dot">.xyz</span>
          </a>
          <nav class="footer-links" aria-label="Footer navigation">
            <a href="#approach">Approach</a>
            <a href="#health">Healthy living</a>
            <a href="#investors">Investors</a>
            <a
              href="https://foss.global/#/repo/maintainable.xyz/house-spec"
              target="_blank"
              rel="noopener"
            >
              House spec
            </a>
          </nav>
          <div class="legal">
            © 2014 Task Venture Capital GmbH ·
            <a class="legal-link" href="https://legal.task.vc">Legal information</a>
          </div>
        </div>
      </footer>
    `;
  }
}
